import { useState, useRef, useCallback, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { attachDistances, formatDistance } from '../utils/geolocation';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { ModeSwitcher } from './BeeConnect';
import { useApp } from '../contexts/AppContext';
import { EMPTY_NUDGES, getEmptyNudges } from '../utils/engagement';
import { QUIZ_QUESTIONS, getQuizQuestions, CATEGORY_META, getCategories, compatLabel } from '../utils/matching';
import { BadgePill, LocationIcon, WorkIcon } from '../components/icons';
import { getPublicBadges, computeCoherence } from '../utils/badges';
import { StreakPill, MilestoneCelebration, useMilestoneChecker } from '../components/Gamification';
import { getJourneyStage } from '../utils/beeJourney';
import FoundingBadge from '../components/FoundingBadge';
import { getBlockedIdsSync as getBlockedIds } from '../utils/safety';
import ProfileSheet   from '../components/ProfileSheet';
import ReferralSheet  from '../components/ReferralSheet';

// ── Inline quiz interstitial ───────────────────────────────
// Quiz stages: 3 labeled phases for progressive onboarding
const QUIZ_STAGES = [
  { id:'lifestyle', range:[0,3], emoji:'✦', labelFr:'Votre quotidien', labelEn:'Your daily life',   color:'#9CA880' },
  { id:'relational', range:[4,6], emoji:'✦', labelFr:'En relation',     labelEn:'In relationships', color:'#7AB8C4' },
  { id:'identity',  range:[7,9], emoji:'✦', labelFr:'Qui vous êtes',   labelEn:'Who you are',      color:'#C9A84C' },
];

function QuizInterstitial({
  onComplete, onSkip, initialAnswers={}, initialIdx=0, lang='en'
}) {
  const { savePartialQuiz } = useApp();
  const tr = useTr();
  const [qIdx, setQIdx] = useState(initialIdx);
  const [answers, setAnswers] = useState(initialAnswers);
  const [stageDone, setStageDone] = useState(null); // stage shown at end-of-stage transition

  const allQuestions = getQuizQuestions(lang);
  const q = allQuestions[qIdx];
  const currentStage = QUIZ_STAGES.find(s => qIdx >= s.range[0] && qIdx <= s.range[1]);
  const stageIdx = QUIZ_STAGES.indexOf(currentStage);
  const isStageEnd = currentStage && qIdx === currentStage.range[1];
  const isLast = qIdx === allQuestions.length - 1;

  const handleSelect = (value) => {
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    savePartialQuiz?.(next, qIdx);
    if (isLast) {
      onComplete(next);
    } else if (isStageEnd && !isLast) {
      setStageDone(currentStage);
    } else {
      setTimeout(() => setQIdx(i => i + 1), 220);
    }
  };

  // Inter-stage transition screen
  if (stageDone) {
    const nextStage = QUIZ_STAGES[QUIZ_STAGES.indexOf(stageDone) + 1];
    return (
      <div style={{ padding:'2rem 1.5rem', textAlign:'center', minHeight:320, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'1rem' }}>
        <div style={{ fontSize:'2.5rem' }}>{stageDone.emoji}</div>
        <p style={{ fontSize:'0.6rem', letterSpacing:'0.18em', textTransform:'uppercase', color:stageDone.color }}>
          {lang === 'fr' ? 'Étape complétée' : 'Stage complete'}
        </p>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, lineHeight:1.3, margin:0 }}>
          {lang === 'fr' ? stageDone.labelFr : stageDone.labelEn}
        </h2>
        <div style={{ display:'flex', gap:'0.4rem', margin:'0.5rem 0' }}>
          {QUIZ_STAGES.map((s, i) => (
            <div key={s.id} style={{ width:28, height:3, borderRadius:2, background: i <= stageIdx ? s.color : 'var(--border)', transition:'background 0.3s' }}/>
          ))}
        </div>
        {nextStage && (
          <>
            <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6, maxWidth:260 }}>
              {lang === 'fr' ? `Prochaine étape : ${nextStage.labelFr}` : `Next: ${nextStage.labelEn}`}
            </p>
            <button
              onClick={() => { setStageDone(null); setQIdx(stageDone.range[1] + 1); }}
              style={{ padding:'0.75rem 1.5rem', background:'linear-gradient(135deg,var(--gold),#B8922A)', border:'none', color:'var(--black)', fontFamily:'Outfit', fontSize:'0.82rem', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', borderRadius:'var(--r-sm)' }}
            >
              {lang === 'fr' ? 'Continuer' : 'Continue'} →
            </button>
            <button onClick={() => onSkip(answers, stageDone.range[1] + 1)}
              style={{ background:'none', border:'none', color:'var(--muted2)', fontSize:'0.7rem', cursor:'pointer', fontFamily:'Outfit' }}>
              {lang === 'fr' ? 'Terminer plus tard' : 'Finish later'}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding:'1rem 1.2rem 2rem' }}>
      {/* 3-stage progress bar */}
      <div style={{ display:'flex', gap:'0.3rem', marginBottom:'1rem' }}>
        {QUIZ_STAGES.map((s, i) => {
          const done = i < stageIdx;
          const active = i === stageIdx;
          const pct = active ? ((qIdx - s.range[0] + 1) / (s.range[1] - s.range[0] + 1) * 100) : (done ? 100 : 0);
          return (
            <div key={s.id} style={{ flex:1, height:3, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${pct}%`, background:s.color, transition:'width 0.3s' }}/>
            </div>
          );
        })}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span style={{ fontSize:'0.9rem' }}>{currentStage?.emoji}</span>
          <span style={{ fontSize:'0.6rem', letterSpacing:'0.14em', textTransform:'uppercase', color:currentStage?.color || 'var(--gold)' }}>
            {lang === 'fr' ? `Étape ${stageIdx+1}/3 · ${currentStage?.labelFr}` : `Stage ${stageIdx+1}/3 · ${currentStage?.labelEn}`}
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.8rem' }}>
          <span style={{ fontSize:'0.68rem', color:'var(--muted2)' }}>{qIdx+1}/{allQuestions.length}</span>
          <button onClick={() => onSkip(answers, qIdx)} style={{ background:'none', border:'none', color:'var(--muted2)', fontSize:'0.72rem', cursor:'pointer', fontFamily:'Outfit' }}>{tr('discover.skip_now')}</button>
        </div>
      </div>
      <div style={{ marginBottom:'0.5rem', opacity:0.6 }}>{q.emoji}</div>
      <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.35rem', fontWeight:400, lineHeight:1.25, marginBottom:'1rem' }}>{q.question}</h2>
      <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
        {q.options.map(opt => (
          <button key={opt.value} onClick={() => handleSelect(opt.value)} style={{ padding:'0.7rem 0.9rem', background: answers[q.id]===opt.value ? 'var(--gold-pale)' : 'var(--dark)', border:`1px solid ${answers[q.id]===opt.value ? 'var(--gold)' : 'var(--border)'}`, color: answers[q.id]===opt.value ? 'var(--gold)' : 'var(--white)', fontFamily:'Outfit', fontSize:'0.82rem', lineHeight:1.4, cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}>{opt.label}</button>
        ))}
      </div>
      {qIdx > 0 && <button onClick={() => setQIdx(i=>i-1)} style={{ background:'none', border:'none', color:'var(--muted2)', cursor:'pointer', fontSize:'0.75rem', marginTop:'0.8rem', fontFamily:'Outfit' }}>{tr('discover.prev_btn')}</button>}
    </div>
  );
}


export default function Discover() {
  
  const tr = useTr();
  const {
    currentUser, loading, users, candidates, location, requestUserLocation,
    likes, passes, superLikes, likeUser, passUser, superLikeUser, rewindLastPass,
    matches, lang, conversations, setActiveChat,
    features, subsEnabled, featureMatrix, dailyUsage,
    getCompatibility, boost,
    showQuizPrompt, setShowQuizPrompt, saveQuizAnswers, savePartialQuiz,
    quizDone, partialQuizAnswers, quizResumeIdx, activeNetwork, appMode, setPage,
    discoveryPrefs, setDiscoveryPrefs,
  } = useApp();
  const { checkMilestone, checkStreaks, pending, clearPending } = useMilestoneChecker();

  
  const [discoverMode, setDiscoverMode] = useState('romantic');
  const [idx, setIdx] = useState(0);
  const [swipeDir, setSwipeDir] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [detailUser, setDetailUser] = useState(null); // override user shown in detail sheet
  // Local filter state — independent per page so filtering Discover never
  // empties BeeHive or BeeConnect.
  const [filter, setFilter] = useState('all');
  const [showAdvFilters, setShowAdvFilters] = useState(false);
  // Local copy of prefs while sheet is open — committed on "Apply"
  const [draftPrefs, setDraftPrefs] = useState(null);
  const touchStart = useRef(null);
  const [showReport,   setShowReport]   = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [showReferral, setShowReferral] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const nudge = EMPTY_NUDGES[Math.floor(Math.random() * EMPTY_NUDGES.length)];
  const [matchOverlay, setMatchOverlay] = useState(null);
  // "Who liked me" — fetched from likes table
  const [likedMeUsers, setLikedMeUsers] = useState([]);
  const [showLikedMe, setShowLikedMe] = useState(false);

  // ── Profile view tracking ────────────────────────────────────────────────
  const [viewedTodayIds, setViewedTodayIds] = useState(new Set());
  const [hasLooped,      setHasLooped]      = useState(false);
  const [dailyCount,     setDailyCount]     = useState(0);
  const profileStartTime = useRef(Date.now());
  const loopQueueRef     = useRef([]);
  const unseenTodayRef   = useRef([]);
  const seenTodayRef     = useRef([]);

  // Persist shown match IDs so they survive navigation AND page refresh.
  const shownMatchIds = useRef(new Set(
    JSON.parse(localStorage.getItem('beeShownMatches') || '[]')
  ));
  // Timestamp of the current login session — only matches created after this
  // moment are eligible for the overlay. Pre-existing seed matches are ignored.
  const loginTimestamp = useRef(Date.now());

  useEffect(() => {
    if (loading) return;
    // Only surface matches that are:
    //   1. not yet shown to this user
    //   2. created AFTER the current login session started (< 5 min ago as a buffer)
    const newMatch = matches.find(m => {
      if (shownMatchIds.current.has(m.matchId)) return false;
      const matchAge = Date.now() - new Date(m.created_at || 0).getTime();
      // Seal anything older than 5 minutes on this session — it's a pre-existing match
      return matchAge < 300_000 && new Date(m.created_at || 0).getTime() > loginTimestamp.current - 10_000;
    });
    if (newMatch) {
      shownMatchIds.current.add(newMatch.matchId);
      localStorage.setItem('beeShownMatches', JSON.stringify([...shownMatchIds.current]));
      setMatchOverlay(newMatch);
    }
    // Always stamp every known match as seen so they never appear in future sessions
    matches.forEach(m => shownMatchIds.current.add(m.matchId));
  }, [matches, loading]);

  // Quiz interstitial now triggered by AppContext after first match

  // Fetch users who liked me (Honey+ sees full list; free sees blurred count)
  useEffect(() => {
    if (!currentUser?.id) return;
    import('../utils/supabase').then(({ supabase }) => {
      supabase?.from('likes')
        .select('from_user')
        .eq('to_user', currentUser.id)
        .then(({ data }) => {
          if (!data?.length) return;
          const ids = data.map(r => r.from_user);
          // Fetch their basic profile info
          supabase?.from('users')
            .select('id,name,age,photos,job,verified')
            .in('id', ids)
            .then(({ data: profiles }) => {
              if (profiles) setLikedMeUsers(profiles);
            });
        });
    });
  }, [currentUser?.id]);

  // Fetch today's viewed profile IDs from Supabase
  useEffect(() => {
    if (!currentUser?.id) return;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    import('../utils/supabase').then(({ supabase }) => {
      supabase?.from('profile_views')
        .select('profile_id')
        .eq('viewer_id', currentUser.id)
        .gte('seen_at', todayStart.toISOString())
        .then(({ data }) => {
          if (data?.length) {
            setViewedTodayIds(new Set(data.map(r => r.profile_id)));
            setDailyCount(data.length);
          }
        });
    });
  }, [currentUser?.id]);

  // Reset profile timer whenever the displayed profile changes
  useEffect(() => {
    profileStartTime.current = Date.now();
  }, [idx, hasLooped]);

  // Edge case: if all candidates were already viewed today (e.g. returning user),
  // start the loop immediately instead of showing an instant empty state
  useEffect(() => {
    if (hasLooped || dailyCount === 0) return;
    if (unseenTodayRef.current.length === 0 && seenTodayRef.current.length > 0) {
      loopQueueRef.current = [...seenTodayRef.current].sort(() => Math.random() - 0.5);
      setHasLooped(true);
      setIdx(0);
    }
  }, [viewedTodayIds.size, hasLooped, dailyCount]);

  // Apply network filter first
  const networkUsers = users || [];  // users = filtered network candidates from AppContext
  const blockedIds = getBlockedIds(currentUser?.id || '');
  // Liked and blocked profiles are permanently excluded.
  // Passed profiles come back at the end of the queue (slow-dating: reconsider).
  const passTs = (() => { try { return JSON.parse(localStorage.getItem('beelovePassTs') || '{}'); } catch { return {}; } })();
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  const unseenQueue = networkUsers.filter(u => !likes.has(u.id) && !blockedIds.has(u.id) && !passes.has(u.id) && !u.paused && !u.incognito);
  const passedBack  = networkUsers.filter(u => !likes.has(u.id) && !blockedIds.has(u.id) &&  passes.has(u.id) && !u.paused && !u.incognito && (Date.now() - (passTs[u.id] || 0)) >= SEVEN_DAYS);
  const queue = [...unseenQueue, ...passedBack];
  const queueWithDist = location
    ? queue.map(u => u.lat && u.lng
        ? { ...u, distance: Math.round(((u.lat - location.lat)**2 + (u.lng - location.lng)**2)**0.5 * 111) }
        : u)
    : queue;
  const withCompat = queueWithDist.map(u => ({
    ...u,
    _compat: getCompatibility(u.id),
  }));

  const prefs = discoveryPrefs || { minAge: 18, maxAge: 55, radiusKm: 25 };
  const advGoal = prefs.relationshipGoal || 'any';
  const advDiet = prefs.diet || 'any';
  const advExercise = prefs.exercise || 'any';

  const filtered = withCompat
    .filter(u => {
      // Quick-filter pills
      if (filter === 'compatible') { if (u._compat?.score != null && u._compat.score < 50) return false; }
      if (filter === 'nearby')     { if (!u.distance || u.distance > 15) return false; }
      if (filter === 'verified')   { if (!u.verified) return false; }
      if (filter === 'friends')    { if (u.mood !== 'connecting' && !u.seekingFriends) return false; }
      // Advanced prefs (age + lifestyle)
      if (u.age && (u.age < prefs.minAge || u.age > prefs.maxAge)) return false;
      if (advGoal !== 'any' && u.relationship_goal && u.relationship_goal !== advGoal) return false;
      if (advDiet !== 'any' && u.diet && u.diet !== advDiet) return false;
      if (advExercise !== 'any' && u.exercise && u.exercise !== advExercise) return false;
      return true;
    })
    .sort((a, b) => {
      if (filter === 'compatible' && a._compat && b._compat) return b._compat.score - a._compat.score;
      return 0;
    });

  // Count active advanced filters for badge
  const advActiveCount = [
    prefs.minAge !== 18 || prefs.maxAge !== 55,
    advGoal !== 'any',
    advDiet !== 'any',
    advExercise !== 'any',
  ].filter(Boolean).length;

  // Daily view limit by plan
  const DAILY_LIMITS = { free: 30, honey: 100, gold: 9999, royal: 9999, founder: 9999 };
  const DAILY_LIMIT  = DAILY_LIMITS[currentUser?.plan] ?? 30;
  const limitReached = dailyCount >= DAILY_LIMIT;

  // Split filtered into unseen-today and seen-today; keep refs for doSwipe closure
  unseenTodayRef.current = filtered.filter(u => !viewedTodayIds.has(u.id));
  seenTodayRef.current   = filtered.filter(u =>  viewedTodayIds.has(u.id));

  const displayQueue = hasLooped ? loopQueueRef.current : unseenTodayRef.current;
  const user = limitReached ? undefined : displayQueue[idx];
  const next = limitReached ? undefined : displayQueue[idx + 1];

  const recordView = (profileId) => {
    if (!currentUser?.id || !profileId) return;
    const ms = Date.now() - profileStartTime.current;
    setViewedTodayIds(prev => new Set([...prev, profileId]));
    setDailyCount(c => c + 1);
    import('../utils/supabase').then(({ supabase }) => {
      supabase?.from('profile_views').upsert(
        { viewer_id: currentUser.id, profile_id: profileId, seen_at: new Date().toISOString(), time_spent_ms: ms },
        { onConflict: 'viewer_id,profile_id' }
      );
    });
  };

  const doSwipe = useCallback((dir) => {
    if (!user) return;
    recordView(user.id);
    setSwipeDir(dir);
    if (dir === 'right') { likeUser(user.id); checkMilestone('first_like'); checkStreaks(); }
    else if (dir === 'left') passUser(user.id);
    else if (dir === 'up') { superLikeUser(user.id); checkMilestone('first_super_like'); }
    setTimeout(() => {
      setSwipeDir(null);
      setPhotoIdx(0);
      setIdx(prev => {
        const nextIdx = prev + 1;
        // First pass exhausted — start loop if seen profiles exist
        if (!hasLooped && !unseenTodayRef.current[nextIdx]) {
          const seen = seenTodayRef.current;
          if (seen.length > 0) {
            loopQueueRef.current = [...seen].sort(() => Math.random() - 0.5);
            setHasLooped(true);
            return 0;
          }
        }
        return nextIdx;
      });
    }, 380);
  }, [user, hasLooped, likeUser, passUser, superLikeUser, checkMilestone, checkStreaks]);

  // Touch swipe
  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      doSwipe(dx > 0 ? 'right' : 'left');
    } else if (dy < -80) {
      doSwipe('up');
    }
    touchStart.current = null;
  };

  const cardStyle = () => {
    if (!swipeDir) return {};
    if (swipeDir === 'right') return { transform:'translateX(120%) rotate(15deg)', transition:'transform 0.38s ease' };
    if (swipeDir === 'left')  return { transform:'translateX(-120%) rotate(-15deg)', transition:'transform 0.38s ease' };
    if (swipeDir === 'up')    return { transform:'translateY(-120%)', transition:'transform 0.38s ease' };
    return {};
  };

  // Empty-state computation — does NOT short-circuit the render so that the
  // filter pills (and advanced-filter button) stay visible at the top of the
  // page even when the current selection returns zero profiles.
  const fr = lang === 'fr';
  const isLimit = limitReached && DAILY_LIMIT < 9999;
  const advFiltersOn = advActiveCount > 0;
  const filterActive = filter !== 'all' || advFiltersOn;
  const unfilteredHasMore = withCompat.length > 0;
  const isFilterEmpty = !user && filterActive && unfilteredHasMore && !isLimit;
  const isExhausted   = !user && !isFilterEmpty;

  const scoreColor = !user ? 'var(--muted)' : (user._compat?.score||0) >= 90 ? 'var(--gold)' : (user._compat?.score||0) >= 75 ? '#9CA880' : (user._compat?.score||0) >= 55 ? '#7AB8C4' : 'var(--muted)';

  return (
    <div style={{ padding:'0.75rem' }}>

      {/* ── Quiz interstitial overlay ── */}
      {showQuizPrompt && (
        <div style={{ position:'fixed', inset:0, zIndex:600, display:'flex', alignItems:'flex-end', justifyContent:'center', background:'rgba(0,0,0,0.7)', backdropFilter:'blur(4px)' }}>
          <div style={{ width:'100%', maxWidth:480, maxHeight:'calc(92vh - var(--bottom-h, 56px))', display:'flex', flexDirection:'column', background:'var(--black)', borderTop:'1px solid var(--border)', borderLeft:'1px solid var(--border)', borderRight:'1px solid var(--border)', borderRadius:'var(--r-xl) var(--r-xl) 0 0', marginBottom:'var(--bottom-h, 56px)', animation:'sheetUp 0.3s cubic-bezier(0.32,0.72,0,1)' }}>
            <div style={{ padding:'1.2rem 1.5rem 0.8rem', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', fontWeight:500 }}>
                <span style={{ color:'var(--gold)', fontStyle:'italic' }}>Bee</span>Love — {tr('matches.compat_section')}
              </div>
              <button onClick={() => setShowQuizPrompt(false)} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.2rem', lineHeight:1 }}>✕</button>
            </div>
            {Object.keys(partialQuizAnswers).length > 0 ? (
              <div style={{ padding:'0.8rem 1.5rem 0.5rem', background:'var(--gold-pale)', borderBottom:'1px solid var(--gold-border)' }}>
                <p style={{ fontSize:'0.82rem', color:'var(--white)', lineHeight:1.6, fontWeight:500 }}>
                  {tr('quiz.resume_prompt')}
                </p>
                <p style={{ fontSize:'0.75rem', color:'var(--gold)', marginTop:'0.2rem' }}>
                  {tr('discover.quiz_left_1')}<strong>{getQuizQuestions(lang).length - quizResumeIdx}</strong>{tr('discover.quiz_left_2',{plural: getQuizQuestions(lang).length - quizResumeIdx > 1 ? 's' : ''})}
                </p>
              </div>
            ) : (
              <p style={{ padding:'0.6rem 1.5rem 0.4rem', fontSize:'0.78rem', color:'var(--muted)' }}>
                {tr('quiz.match_prompt')}
              </p>
            )}
            <div style={{ flex:1, overflowY:'auto' }}>
              <QuizInterstitial
                lang={lang}
                onComplete={(answers) => { saveQuizAnswers(answers); setShowQuizPrompt(false); }}
                onSkip={(partialAnswers, idx) => { savePartialQuiz(partialAnswers, idx); setShowQuizPrompt(false); }}
                initialAnswers={partialQuizAnswers}
                initialIdx={quizResumeIdx}
              />
            </div>
          </div>
        </div>
      )}

      {/* Advanced filters sheet */}
      {showAdvFilters && (() => {
        const d = draftPrefs || { ...prefs };
        const set = (k, v) => setDraftPrefs(p => ({ ...(p || prefs), [k]: v }));
        const GOALS = ['any','relationship','casual','friendship','marriage','unsure'];
        const DIETS = ['any','omnivore','vegetarian','vegan','halal','kosher','other'];
        const EXERS = ['any','daily','3-4x/week','1-2x/week','rarely','never'];
        const labelGoal = { any:tr('common.any')||'Any', relationship:'Relationship', casual:'Casual', friendship:'Friendship', marriage:'Marriage', unsure:'Unsure' };
        const labelDiet = { any:tr('common.any')||'Any', omnivore:'Omnivore', vegetarian:'Vegetarian', vegan:'Vegan', halal:'Halal', kosher:'Kosher', other:'Other' };
        const labelExer = { any:tr('common.any')||'Any', daily:'Daily', '3-4x/week':'3-4×/wk', '1-2x/week':'1-2×/wk', rarely:'Rarely', never:'Never' };
        return (
          <div onClick={() => setShowAdvFilters(false)}
            style={{ position:'fixed', inset:0, zIndex:500, background:'rgba(0,0,0,0.65)', backdropFilter:'blur(6px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ width:'100%', maxWidth:480, background:'var(--black)', border:'1px solid var(--border)', borderBottom:'none', borderRadius:'var(--r-xl) var(--r-xl) 0 0', padding:'0 0 var(--bottom-h,56px)', maxHeight:'88vh', overflowY:'auto' }}>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.2rem 0.8rem', borderBottom:'1px solid var(--border)' }}>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'var(--white)' }}>{tr('discover.adv_filters')||'Advanced filters'}</span>
                <button onClick={() => { setDraftPrefs(null); setShowAdvFilters(false); }} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.2rem', lineHeight:1 }}>✕</button>
              </div>
              <div style={{ padding:'1.2rem' }}>
                {/* Age range */}
                <div style={{ marginBottom:'1.4rem' }}>
                  <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>
                    {tr('discover.age_range')||'Age range'} — {d.minAge||18}–{d.maxAge||55}
                  </div>
                  <div style={{ display:'flex', gap:'0.8rem', alignItems:'center' }}>
                    <span style={{ fontSize:'0.72rem', color:'var(--muted)', minWidth:24 }}>{d.minAge||18}</span>
                    <input type="range" min={18} max={d.maxAge||55} value={d.minAge||18}
                      onChange={e => set('minAge', Number(e.target.value))}
                      style={{ flex:1, accentColor:'var(--gold)' }}/>
                    <input type="range" min={d.minAge||18} max={75} value={d.maxAge||55}
                      onChange={e => set('maxAge', Number(e.target.value))}
                      style={{ flex:1, accentColor:'var(--gold)' }}/>
                    <span style={{ fontSize:'0.72rem', color:'var(--muted)', minWidth:24 }}>{d.maxAge||55}</span>
                  </div>
                </div>
                {/* Distance */}
                <div style={{ marginBottom:'1.4rem' }}>
                  <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>
                    {tr('discover.max_distance')||'Max distance'} — {d.radiusKm||25} km
                  </div>
                  <input type="range" min={5} max={150} step={5} value={d.radiusKm||25}
                    onChange={e => set('radiusKm', Number(e.target.value))}
                    style={{ width:'100%', accentColor:'var(--gold)' }}/>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.62rem', color:'var(--muted2)', marginTop:'0.2rem' }}>
                    <span>5 km</span><span>150 km</span>
                  </div>
                </div>
                {/* Relationship goal */}
                <div style={{ marginBottom:'1.4rem' }}>
                  <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>
                    {tr('profile.relationship_goal')||'Relationship goal'}
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
                    {GOALS.map(g => (
                      <button key={g} onClick={() => set('relationshipGoal', g)}
                        style={{ padding:'0.28rem 0.7rem', border:`1px solid ${(d.relationshipGoal||'any')===g ? 'var(--gold)' : 'var(--border)'}`, background:(d.relationshipGoal||'any')===g ? 'var(--gold-pale)' : 'transparent', color:(d.relationshipGoal||'any')===g ? 'var(--gold)' : 'var(--muted)', fontSize:'0.7rem', cursor:'pointer', borderRadius:99, fontFamily:'Outfit', transition:'all 0.15s' }}>
                        {labelGoal[g]}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Diet */}
                <div style={{ marginBottom:'1.4rem' }}>
                  <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>
                    {tr('profile.diet')||'Diet'}
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
                    {DIETS.map(g => (
                      <button key={g} onClick={() => set('diet', g)}
                        style={{ padding:'0.28rem 0.7rem', border:`1px solid ${(d.diet||'any')===g ? 'var(--gold)' : 'var(--border)'}`, background:(d.diet||'any')===g ? 'var(--gold-pale)' : 'transparent', color:(d.diet||'any')===g ? 'var(--gold)' : 'var(--muted)', fontSize:'0.7rem', cursor:'pointer', borderRadius:99, fontFamily:'Outfit', transition:'all 0.15s' }}>
                        {labelDiet[g]}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Exercise */}
                <div style={{ marginBottom:'1.6rem' }}>
                  <div style={{ fontSize:'0.7rem', fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>
                    {tr('profile.exercise')||'Exercise'}
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
                    {EXERS.map(g => (
                      <button key={g} onClick={() => set('exercise', g)}
                        style={{ padding:'0.28rem 0.7rem', border:`1px solid ${(d.exercise||'any')===g ? 'var(--gold)' : 'var(--border)'}`, background:(d.exercise||'any')===g ? 'var(--gold-pale)' : 'transparent', color:(d.exercise||'any')===g ? 'var(--gold)' : 'var(--muted)', fontSize:'0.7rem', cursor:'pointer', borderRadius:99, fontFamily:'Outfit', transition:'all 0.15s' }}>
                        {labelExer[g]}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Actions */}
                <div style={{ display:'flex', gap:'0.6rem' }}>
                  <button onClick={() => { setDiscoveryPrefs({ minAge:18, maxAge:55, radiusKm:25, relationshipGoal:'any', diet:'any', exercise:'any' }); setDraftPrefs(null); setShowAdvFilters(false); setIdx(0); }}
                    style={{ flex:1, padding:'0.65rem', border:'1px solid var(--border)', background:'transparent', color:'var(--muted)', fontSize:'0.78rem', cursor:'pointer', fontFamily:'Outfit', borderRadius:'var(--r)' }}>
                    {tr('common.reset')||'Reset'}
                  </button>
                  <button onClick={() => { if (draftPrefs) setDiscoveryPrefs(draftPrefs); setDraftPrefs(null); setShowAdvFilters(false); setIdx(0); }}
                    style={{ flex:2, padding:'0.65rem', background:'var(--gold)', border:'none', color:'var(--black)', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', fontFamily:'Outfit', borderRadius:'var(--r)' }}>
                    {tr('common.apply')||'Apply'} {draftPrefs ? `(${[draftPrefs.minAge!==18||draftPrefs.maxAge!==55,draftPrefs.relationshipGoal&&draftPrefs.relationshipGoal!=='any',draftPrefs.diet&&draftPrefs.diet!=='any',draftPrefs.exercise&&draftPrefs.exercise!=='any'].filter(Boolean).length} active)` : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Filter pills */}
      <div style={{ display:'flex', gap:'0.4rem', marginBottom:'0.75rem', overflowX:'auto', paddingBottom:'2px', msOverflowStyle:'none', scrollbarWidth:'none' }}>
        {[
          {
            id:'all', label:tr('discover.filter_all'),
            icon: (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
            ),
          },
          {
            id:'compatible', label:tr('discover.filter_bestmatch'),
            icon: (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            ),
          },
          {
            id:'nearby', label:tr('discover.filter_nearby'),
            icon: (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            ),
          },
          {
            id:'verified', label:tr('discover.filter_verified'),
            icon: (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            ),
          },
          {
            id:'friends', label:tr('discover.open_friends'),
            icon: (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            ),
          },
        ].map(f => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={()=>{ setFilter(f.id); setIdx(0); }}
              style={{
                flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: '0.28rem',
                padding: '0.3rem 0.65rem',
                background: active ? 'var(--gold)' : 'transparent',
                border: `1px solid ${active ? 'transparent' : 'var(--border)'}`,
                borderRadius: '99px',
                color: active ? 'var(--black)' : 'var(--muted)',
                fontSize: '0.68rem',
                fontWeight: active ? 600 : 400,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                transition: 'all 0.18s',
                fontFamily: 'Outfit',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = 'var(--gold-border)'; e.currentTarget.style.color = 'var(--white)'; }}}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--muted)'; }}}
            >
              {f.icon}
              {f.label}
            </button>
          );
        })}
        {/* Advanced filter button */}
        <button
          onClick={() => { setDraftPrefs({ ...prefs }); setShowAdvFilters(true); }}
          style={{
            flexShrink: 0, display:'flex', alignItems:'center', gap:'0.28rem',
            padding:'0.3rem 0.65rem',
            background: advActiveCount > 0 ? 'rgba(201,168,76,0.12)' : 'transparent',
            border: `1px solid ${advActiveCount > 0 ? 'var(--gold-border)' : 'var(--border)'}`,
            borderRadius: '99px', color: advActiveCount > 0 ? 'var(--gold)' : 'var(--muted)',
            fontSize:'0.68rem', fontWeight: advActiveCount > 0 ? 600 : 400,
            letterSpacing:'0.02em', cursor:'pointer', transition:'all 0.18s', fontFamily:'Outfit',
            position:'relative',
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
          </svg>
          {tr('discover.adv_filters')||'Filters'}
          {advActiveCount > 0 && (
            <span style={{ width:14, height:14, background:'var(--gold)', borderRadius:'50%', color:'var(--black)', fontSize:'0.55rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', marginLeft:'0.1rem' }}>
              {advActiveCount}
            </span>
          )}
        </button>
      </div>

      {/* ── Who liked me — gated by perk_see_liked ── */}
      {features?.perk_see_liked && likedMeUsers.length > 0 && (
        <div style={{ marginBottom:'0.75rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', overflow:'hidden' }}>
          <button
            onClick={() => setShowLikedMe(v => !v)}
            style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background:'transparent', border:'none', cursor:'pointer', fontFamily:'Outfit' }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--gold)" stroke="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              <span style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--white)' }}>
                {lang === 'fr' ? `${likedMeUsers.length} personne${likedMeUsers.length > 1 ? 's' : ''} t'a aimé` : `${likedMeUsers.length} person${likedMeUsers.length > 1 ? 's' : ''} liked you`}
              </span>
              {currentUser?.plan === 'free' && (
                <span style={{ fontSize:'0.6rem', padding:'2px 6px', borderRadius:99, background:'var(--gold-pale)', border:'1px solid var(--gold-border)', color:'var(--gold)' }}>
                  {lang === 'fr' ? 'Honey+' : 'Honey+'}
                </span>
              )}
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2" style={{ transform: showLikedMe ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {showLikedMe && (
            <div style={{ display:'flex', gap:'0.6rem', padding:'0 0.85rem 0.85rem', overflowX:'auto' }}>
              {likedMeUsers.map(u => {
                const isPaid = currentUser?.plan !== 'free';
                return (
                  <div
                    key={u.id}
                    onClick={() => isPaid ? (setDetailUser(u), setShowDetail(true)) : setPage('upgrade')}
                    style={{ flexShrink:0, width:72, cursor:'pointer', textAlign:'center' }}
                  >
                    <div style={{ width:64, height:64, borderRadius:'50%', overflow:'hidden', margin:'0 auto', background:'var(--charcoal)', border:'2px solid var(--gold-border)', position:'relative' }}>
                      {u.photos?.[0]
                        ? <img src={u.photos[0]} alt={u.name} style={{ width:'100%', height:'100%', objectFit:'cover', filter: isPaid ? 'none' : 'blur(10px)' }}/>
                        : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.5rem', color:'var(--gold)', opacity:0.4, filter: isPaid ? 'none' : 'blur(6px)' }}>{(u.name||'?')[0]}</span>
                          </div>
                      }
                      {!isPaid && (
                        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </div>
                      )}
                    </div>
                    <p style={{ fontSize:'0.62rem', color: isPaid ? 'var(--white)' : 'var(--muted)', margin:'0.3rem 0 0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {isPaid ? `${u.name}${u.age ? `, ${u.age}` : ''}` : '• • •'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Onboarding checklist — shown until complete */}
      <OnboardingChecklist compact={true} onNavigate={(p) => setPage(p)}/>

      {/* Location prompt */}
      {!location && (
        <div onClick={requestUserLocation}
          style={{ display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 0.7rem', marginBottom:'0.5rem', background:'rgba(91,155,213,0.06)', border:'1px solid rgba(91,155,213,0.18)', borderRadius:8, cursor:'pointer' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(91,155,213,0.9)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
          <span style={{ fontSize:'0.68rem', color:'rgba(91,155,213,0.9)' }}>Enable location to see distances</span>
        </div>
      )}

      {/* Streak pill */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.5rem 1rem 0' }}>
        <StreakPill userId={currentUser?.id}/>
        <div/>
      </div>

      {/* Journey tip banner — gated by discover_journey */}
      {features?.discover_journey && (() => {
        const journey = getJourneyStage(currentUser, conversations, matches, lang);
        const next = journey.nextStage;
        if (!next) return null;
        return (
          <div style={{ margin:'0.5rem 1rem 0', padding:'0.55rem 0.85rem', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', display:'flex', alignItems:'center', gap:'0.6rem' }}>
            <span style={{ fontSize:'1rem', flexShrink:0 }}>🐝</span>
            <div style={{ minWidth:0 }}>
              <p style={{ margin:0, fontSize:'0.68rem', fontWeight:600, color:'var(--gold)' }}>
                {lang === 'fr' ? `Prochaine étape : ${next.name}` : `Next: ${next.name}`}
              </p>
              <p style={{ margin:0, fontSize:'0.61rem', color:'var(--muted)', lineHeight:1.4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {next.desc}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Mode switcher — only when BeeConnect is active */}
      {features?.beeconnect !== false && (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'0.5rem', paddingRight:'0.2rem' }}>
          <ModeSwitcher mode={discoverMode} onSwitch={setDiscoverMode}/>
        </div>
      )}


      {/* Daily Picks mode — replaces card stack when enabled.
          Snapshot once per (user, day) so likes don't shrink the list — the
          heart simply lights up on the persisted picks. */}
      {features?.discover_daily_picks && (() => {
        const todayKey = `beeloveTodayPicks_${currentUser?.id || 'anon'}_${new Date().toISOString().slice(0, 10)}`;
        let pickIds = [];
        try {
          const stored = localStorage.getItem(todayKey);
          if (stored) pickIds = JSON.parse(stored);
        } catch { pickIds = []; }
        if (pickIds.length === 0 && filtered.length > 0) {
          pickIds = filtered.slice(0, 5).map(u => u.id);
          try { localStorage.setItem(todayKey, JSON.stringify(pickIds)); } catch {}
        }
        // Resolve from raw candidates so users we've already liked still appear.
        const byId = new Map((candidates || []).map(u => [u.id, u]));
        const picks = pickIds.map(id => byId.get(id)).filter(Boolean);
        return (
          <div style={{ paddingBottom:'1rem' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
              <div>
                <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', fontWeight:400, color:'var(--white)', margin:0 }}>Today's picks</p>
                <p style={{ fontSize:'0.68rem', color:'var(--muted)', marginTop:'0.15rem' }}>{picks.length} curated · refreshes at midnight</p>
              </div>
              <span style={{ fontSize:'0.62rem', color:'var(--gold)', letterSpacing:'0.08em' }}>
                {new Date().toLocaleDateString('en-CA',{weekday:'short',month:'short',day:'numeric'})}
              </span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
              {picks.map(u => {
                const isLiked = likes.has(u.id);
                const compat  = getCompatibility ? getCompatibility(u.id) : null;
                return (
                  <div key={u.id}
                    onClick={() => { setDetailUser(u); setShowDetail(true); }}
                    style={{
                      background: isLiked ? 'rgba(201,168,76,0.10)' : 'var(--dark)',
                      border: `1px solid ${isLiked ? 'var(--gold)' : 'var(--border)'}`,
                      borderRadius:12, padding:'0.75rem',
                      display:'flex', gap:'0.75rem', alignItems:'center', cursor:'pointer',
                      transition:'background 0.2s, border-color 0.2s',
                    }}>
                    <div style={{ width:50, height:50, borderRadius:8, overflow:'hidden', flexShrink:0, background:'var(--charcoal)' }}>
                      {u.photos?.[0] ? <img src={u.photos[0]} alt={u.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', color:'var(--gold)', opacity:0.3 }}>{(u.name||'?')[0]}</span></div>}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                        <span style={{ fontSize:'0.88rem', fontWeight:600, color: isLiked ? 'var(--gold)' : 'var(--white)' }}>{u.name}, {u.age}</span>
                        {compat && (
                          <span style={{ fontSize:'0.63rem', color: (compat.score1 != null && compat.score2 != null) ? 'rgba(78,201,148,0.85)' : 'var(--gold)' }}>
                            {Math.round((compat.score||0)*10)}%
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize:'0.68rem', color:'var(--muted)', margin:'0.1rem 0 0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.job}</p>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); if (!isLiked) likeUser(u.id); }}
                      title={isLiked ? (lang === 'fr' ? 'Liké' : 'Liked') : (lang === 'fr' ? 'Liker' : 'Like')}
                      style={{
                        width:34, height:34, borderRadius:'50%',
                        background: isLiked ? 'var(--gold)' : 'var(--gold-pale)',
                        border: '1px solid var(--gold-border)',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        cursor: isLiked ? 'default' : 'pointer',
                        flexShrink:0, transition:'background 0.2s',
                      }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill={isLiked?'var(--black)':'none'} stroke={isLiked?'var(--black)':'var(--gold)'} strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    </button>
                  </div>
                );
              })}
              {picks.length === 0 && <div style={{ textAlign:'center', padding:'2rem' }}><p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', color:'var(--white)' }}>You've seen everyone today</p><p style={{ fontSize:'0.78rem', color:'var(--muted)', marginTop:'0.3rem' }}>New picks at midnight.</p></div>}
            </div>
          </div>
        );
      })()}

      {!features?.discover_daily_picks && user && /* Card stack */
      <div style={{ position:'relative', height:520, marginBottom:'1rem' }}>

        {/* Back card */}
        {next && (
          <div style={{
            position:'absolute', inset:0,
            background:'var(--charcoal)',
            border:'1px solid var(--border)',
            transform:'scale(0.94) translateY(14px)',
            zIndex:1, overflow:'hidden',
          }}>
            {next?.photos?.[0] && (
              <img src={next.photos[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', filter:'brightness(0.5)' }}/>
            )}
          </div>
        )}

        {/* Main card */}
        <div
          style={{
            position:'absolute', inset:0, zIndex:2,
            background:'var(--charcoal)', overflow:'hidden',
            border:'1px solid var(--border)',
            ...cardStyle(),
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Photo — click opens profile detail */}
          <div style={{ height:340, position:'relative', overflow:'hidden', cursor:'pointer' }}>
            {features?.discover_quota_visible && subsEnabled !== false && (() => {
              const limit = featureMatrix?.[currentUser?.plan||'free']?.likes_per_day?.limit;
              if (!limit) return null;
              const remaining = Math.max(0, limit - (dailyUsage?.likes||0));
              return (
                <div style={{ position:'absolute', top:10, right:10, zIndex:5, pointerEvents:'none' }}>
                  <span style={{ fontSize:'0.6rem', padding:'2px 8px', background:'rgba(0,0,0,0.7)', borderRadius:99, color: remaining<=2?'#E07070':'rgba(255,255,255,0.55)', fontFamily:'Outfit' }}>
                    {remaining} {remaining===1?'like':'likes'} left
                  </span>
                </div>
              );
            })()}

            {/* Compat score badge — top-right circle */}
            {user._compat?.score != null && (
              <div style={{
                position:'absolute',
                top: features?.discover_quota_visible ? 38 : 10,
                right:10, zIndex:5, pointerEvents:'none',
                width:46, height:46, borderRadius:'50%',
                background:'rgba(0,0,0,0.72)',
                backdropFilter:'blur(6px)',
                border:`2px solid ${scoreColor}`,
                display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center',
              }}>
                <span style={{ fontSize:'0.78rem', fontWeight:700, color:scoreColor, lineHeight:1 }}>
                  {user._compat.score}%
                </span>
                <span style={{ fontSize:'0.38rem', color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:'0.04em', marginTop:1 }}>
                  match
                </span>
              </div>
            )}
            <img
              src={(user.photos || [])[photoIdx] || (user.photos || [])[0] || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&q=80'}
              alt={user.name}
              style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
            />

            {/* Photo nav dots */}
            {(user.photos || []).length > 1 && (
              <div style={{ position:'absolute', top:10, left:10, right:10, display:'flex', gap:3 }}>
                {(user.photos || []).map((_,i) => (
                  <div
                    key={i}
                    onClick={()=>setPhotoIdx(i)}
                    style={{
                      flex:1, height:2,
                      background: i===photoIdx ? 'var(--white)' : 'rgba(255,255,255,0.35)',
                      cursor:'pointer', transition:'background 0.2s',
                    }}
                  />
                ))}
              </div>
            )}

            {/* Photo tap zones */}
            <div style={{ position:'absolute', inset:0, display:'flex' }}>
              <div style={{ flex:1 }} onClick={()=>setPhotoIdx(i=>Math.max(0,i-1))} />
              <div style={{ flex:1 }} onClick={()=>setPhotoIdx(i=>Math.min((user.photos||[]).length-1,i+1))} />
            </div>

            {/* Swipe indicators */}
            {swipeDir === 'right' && (
              <div style={{position:'absolute',top:20,left:16,border:'2px solid var(--green)',color:'var(--green)',padding:'0.3rem 0.8rem',fontFamily:"'Cormorant Garamond',serif",fontSize:'1.4rem',fontWeight:600,letterSpacing:'0.05em',transform:'rotate(-8deg)'}}>{tr('discover.like_btn')}</div>
            )}
            {swipeDir === 'left' && (
              <div style={{position:'absolute',top:20,right:16,border:'2px solid var(--red)',color:'var(--red)',padding:'0.3rem 0.8rem',fontFamily:"'Cormorant Garamond',serif",fontSize:'1.4rem',fontWeight:600,letterSpacing:'0.05em',transform:'rotate(8deg)'}}>{tr('discover.pass_btn')}</div>
            )}
            {swipeDir === 'up' && (
              <div style={{position:'absolute',top:20,left:'50%',transform:'translateX(-50%)',border:'2px solid var(--gold)',color:'var(--gold)',padding:'0.3rem 0.8rem',fontFamily:"'Cormorant Garamond',serif",fontSize:'1.4rem',fontWeight:600,letterSpacing:'0.05em'}}>{tr('discover.star_btn')}</div>
            )}

            {/* Distance / online */}
            <div style={{
              position:'absolute', bottom:10, left:12,
              background:'var(--overlay)', backdropFilter:'blur(8px)',
              borderRadius:'var(--r-pill)',
              padding:'0.2rem 0.55rem', fontSize:'0.7rem', color:'var(--white)',
            }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" style={{flexShrink:0}}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>{user.distance === 0 ? 'Near you' : `${user.distance} km`}
            </div>
            <div style={{
              position:'absolute', bottom:10, right:12,
              background:'var(--overlay)', backdropFilter:'blur(8px)',
              borderRadius:'var(--r-pill)',
              padding:'0.2rem 0.55rem', fontSize:'0.7rem', color:'rgba(255,255,255,0.6)',
            }}>
              {user.lastActive}
            </div>
            {user.mood && (() => {
              const md = getMoodById(user.mood);
              return (
                <div style={{ fontSize:'0.6rem', color:md.color, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', border:`1px solid ${md.color}55`, padding:'0.15rem 0.5rem', display:'inline-block' }}>
                  {md.emoji} {md.label}
                </div>
              );
            })()}
            {user.available_until && new Date(user.available_until) > new Date() && (
              <div style={{ position:'absolute', top:10, left:10, fontSize:'0.62rem', color:'#4EC994', background:'rgba(0,0,0,0.65)', backdropFilter:'blur(4px)', border:'1px solid rgba(78,201,148,0.45)', borderRadius:99, padding:'0.2rem 0.55rem', display:'flex', alignItems:'center', gap:'0.3rem' }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:'#4EC994', display:'inline-block', animation:'pulse 1.5s ease-in-out infinite' }}/>
                Disponible ce soir
              </div>
            )}

            {/* Gradient */}
            <div style={{ position:'absolute', bottom:0, left:0, right:0, height:100, background:'linear-gradient(transparent, rgba(0,0,0,0.6))' }} />
          </div>

          {/* Card body */}
          <div style={{ padding:'1rem 1.1rem 0.8rem', cursor:'pointer' }} onClick={()=>setShowDetail(true)}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.2rem' }}>
              <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:500, color:'var(--white)' }}>{user.name}</span>
              <span style={{ color:'var(--muted)', fontSize:'0.95rem' }}>{user.age}</span>
              <FoundingBadge user={user} size="xs" lang={lang}/>
              <span style={{ marginLeft:'auto', color:'var(--muted)', fontSize:'0.75rem' }}>{user.job}</span>
            </div>

            {/* Compat score + source label */}
            {(() => {
              const compat = user._compat;
              if (!compat?.score) return null;
              const hasFriendData = compat.score1 != null && compat.score2 != null;
              const pct  = Math.round(compat.score);
              const rCount = user.friend_rating_count;
              return (
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:'0.4rem' }}>
                  <span style={{ fontSize:'0.65rem', fontWeight:700, color:'var(--gold)', letterSpacing:'0.04em' }}>
                    {pct}% {lang === 'fr' ? 'compat' : 'match'}
                  </span>
                  <span style={{ fontSize:'0.6rem', color:'var(--muted)', opacity:0.7 }}>·</span>
                  <span style={{ fontSize:'0.6rem', color: hasFriendData ? 'rgba(78,201,148,0.8)' : 'var(--muted)', opacity: hasFriendData ? 1 : 0.7 }}>
                    {hasFriendData
                      ? (rCount
                          ? (lang === 'fr' ? `${rCount} avis d'amis` : `${rCount} friend ratings`)
                          : (lang === 'fr' ? 'basé sur avis d\'amis' : 'based on friend ratings'))
                      : (lang === 'fr' ? 'estimé' : 'estimated')}
                  </span>
                </div>
              );
            })()}

            {/* Badges row */}
            {getPublicBadges(user, {}, lang).length > 0 && (
              <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap', marginBottom:'0.5rem' }}>
                {getPublicBadges(user, {}, lang).map(b => <BadgePill key={b.id} badge={b} size="sm"/>)}
              </div>
            )}

            <p style={{ fontSize:'0.83rem', color:'var(--muted)', lineHeight:1.55, marginBottom:'0.6rem',
              display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
              {user.bio}
            </p>

            {/* Interests */}
            <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
              {(user.interests || []).slice(0,3).map((t,i)=>(
                <span key={i} className="chip" style={{ fontSize:'0.68rem' }}>{tr(t)||t}</span>
              ))}
              {(user.interests || []).length > 3 && (
                <span className="chip" style={{ fontSize:'0.68rem', color:'var(--muted2)' }}>+{(user.interests||[]).length-3}</span>
              )}
            </div>

            {/* Icebreakers — gated by discover_icebreakers */}
            {features?.discover_icebreakers && (() => {
              const starters = [];
              if (user.prompt1_q) starters.push(`${user.prompt1_q}${user.prompt1_a ? ` — "${user.prompt1_a.slice(0,50)}${user.prompt1_a.length>50?'…':''}"` : ''}`);
              if (user.prompt2_q && starters.length < 2) starters.push(`${user.prompt2_q}${user.prompt2_a ? ` — "${user.prompt2_a.slice(0,50)}${user.prompt2_a.length>50?'…':''}"` : ''}`);
              if (!starters.length && (user.interests||[]).length) starters.push(lang==='fr' ? `Tu aimes aussi "${tr(user.interests[0])||user.interests[0]}" ?` : `You're both into "${tr(user.interests[0])||user.interests[0]}"?`);
              if (!starters.length) return null;
              return (
                <div style={{ marginTop:'0.55rem', padding:'0.45rem 0.65rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)' }}>
                  <p style={{ margin:'0 0 0.25rem', fontSize:'0.56rem', textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--muted2)' }}>
                    {lang==='fr' ? 'Pour briser la glace' : 'Conversation starters'}
                  </p>
                  {starters.map((s,i) => (
                    <p key={i} style={{ margin:0, fontSize:'0.67rem', color:'var(--muted)', lineHeight:1.45 }}>· {s}</p>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      }

      {/* Empty state — pills above stay visible so the user can change filter */}
      {!features?.discover_daily_picks && isFilterEmpty && (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'2.5rem 1.5rem 1rem', gap:'0.8rem', textAlign:'center' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.2rem', color:'var(--gold)' }}>·</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:300, color:'var(--white)', margin:0 }}>
            {fr ? 'Aucun profil avec ce filtre' : 'No profiles match this filter'}
          </h2>
          <p style={{ color:'var(--muted)', fontSize:'0.85rem', lineHeight:1.6, maxWidth:'24rem', margin:0 }}>
            {fr
              ? `${withCompat.length} profil${withCompat.length > 1 ? 's' : ''} disponible${withCompat.length > 1 ? 's' : ''} sans ce filtre.`
              : `${withCompat.length} profile${withCompat.length > 1 ? 's' : ''} available without this filter.`}
          </p>
          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', justifyContent:'center' }}>
            <button
              onClick={() => { setFilter('all'); setIdx(0); }}
              style={{ padding:'0.5rem 1.1rem', background:'var(--gold)', border:'none', borderRadius:8, color:'var(--black)', fontFamily:'Outfit', fontWeight:600, fontSize:'0.78rem', cursor:'pointer' }}
            >
              {fr ? 'Effacer le filtre' : 'Clear filter'}
            </button>
            {advFiltersOn && (
              <button
                onClick={() => { setDraftPrefs({ ...prefs }); setShowAdvFilters(true); }}
                style={{ padding:'0.5rem 1.1rem', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.78rem', cursor:'pointer' }}
              >
                {fr ? 'Modifier les filtres avancés' : 'Adjust advanced filters'}
              </button>
            )}
          </div>
        </div>
      )}

      {!features?.discover_daily_picks && isExhausted && (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'2.5rem 1.5rem 1rem', gap:'0.9rem', textAlign:'center' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.6rem', color:'var(--gold)' }}>✦</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:300, margin:0 }}>
            {fr ? 'Reviens demain' : 'Come back tomorrow'}
          </h2>
          <p style={{ color:'var(--muted)', fontSize:'0.85rem', lineHeight:1.6, maxWidth:'22rem', margin:0 }}>
            {isLimit
              ? (fr ? `Tu as découvert ${DAILY_LIMIT} profils aujourd'hui — c'est ta limite quotidienne.` : `You've seen ${DAILY_LIMIT} profiles today — that's your daily limit.`)
              : (fr ? "Tu as vu tous les membres disponibles pour aujourd'hui." : "You've seen all available members for today.")}
          </p>
          <button className="btn btn-ghost btn-md" onClick={() => import('../utils/navigate').then(({ navigate }) => navigate('profile'))}>
            {fr ? 'Améliorer mon profil →' : 'Improve my profile →'}
          </button>
        </div>
      )}

      {/* ── Actions ── */}
      {user && <div style={{ display:'flex', alignItems:'center', padding:'0.55rem 1rem 0.5rem' }}>
        {/* Left — rewind (balances right side visually) */}
        <div style={{ flex:1, display:'flex', justifyContent:'flex-start' }}>
          {features?.discover_rewind && (
            <button
              onClick={async () => { const r = await rewindLastPass?.(); if (r?.error) {}}}
              title="Rewind"
              style={{ width:34, height:34, borderRadius:'50%', background:'var(--dark)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--muted)', transition:'background 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='var(--mid)'}
              onMouseLeave={e=>e.currentTarget.style.background='var(--dark)'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.93"/></svg>
            </button>
          )}
        </div>

        {/* Center — pass / super like / like */}
        <div style={{ display:'flex', alignItems:'center', gap:'0.85rem' }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
            <button
              onClick={() => doSwipe('left')}
              title={lang === 'fr' ? 'Passer' : 'Pass'}
              style={{ width:50, height:50, borderRadius:'50%', background:'var(--dark)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--muted)', transition:'background 0.18s' }}
              onMouseEnter={e=>e.currentTarget.style.background='var(--mid)'}
              onMouseLeave={e=>e.currentTarget.style.background='var(--dark)'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            {features?.discover_explicit_btns && <span style={{ fontSize:'0.52rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.04em' }}>{lang==='fr'?'Passer':'Pass'}</span>}
          </div>
          {features?.perk_super_likes !== false && (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
              <button
                onClick={() => doSwipe('up')}
                title={tr('discover.super_like')}
                style={{ width:38, height:38, borderRadius:'50%', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--gold)', transition:'background 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(201,168,76,0.18)'}
                onMouseLeave={e=>e.currentTarget.style.background='var(--gold-pale)'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </button>
              {features?.discover_explicit_btns && <span style={{ fontSize:'0.52rem', color:'var(--gold)', textTransform:'uppercase', letterSpacing:'0.04em' }}>Super</span>}
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
            <button
              onClick={() => doSwipe('right')}
              title={lang === 'fr' ? 'Aimer' : 'Like'}
              style={{ width:50, height:50, borderRadius:'50%', background:'var(--gold)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--black)', transition:'opacity 0.18s' }}
              onMouseEnter={e=>e.currentTarget.style.opacity='0.85'}
              onMouseLeave={e=>e.currentTarget.style.opacity='1'}
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>
            {features?.discover_explicit_btns && <span style={{ fontSize:'0.52rem', color:'var(--gold)', textTransform:'uppercase', letterSpacing:'0.04em' }}>{lang==='fr'?'Aimer':'Like'}</span>}
          </div>
        </div>

        {/* Right — view profile / suggest */}
        <div style={{ flex:1, display:'flex', justifyContent:'flex-end', gap:'0.5rem' }}>
          <button
            onClick={() => setShowDetail(true)}
            title={lang === 'fr' ? 'Voir le profil' : 'View profile'}
            style={{ width:34, height:34, borderRadius:'50%', background:'var(--dark)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--muted)', transition:'background 0.15s' }}
            onMouseEnter={e=>e.currentTarget.style.background='var(--mid)'}
            onMouseLeave={e=>e.currentTarget.style.background='var(--dark)'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
          </button>
          <button
            onClick={() => setShowReferral(true)}
            title={lang === 'fr' ? 'Suggérer' : 'Suggest'}
            style={{ width:34, height:34, borderRadius:'50%', background:'var(--dark)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, color:'var(--muted)', transition:'background 0.15s' }}
            onMouseEnter={e=>e.currentTarget.style.background='var(--mid)'}
            onMouseLeave={e=>e.currentTarget.style.background='var(--dark)'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/>
            </svg>
          </button>
        </div>
      </div>}

      {/* ── Count + discreet report ── */}
      {user && <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.1rem 1rem 0.5rem' }}>
        <p style={{ margin:0, fontSize:'0.65rem', color:'var(--muted2)', letterSpacing:'0.05em' }}>
          {filtered.length - idx - 1} {tr('discover.profiles_remaining')}
        </p>
        <button
          onClick={() => { setShowReport(true); setReportSent(false); setReportReason(''); }}
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--muted2)', fontSize:'0.62rem', display:'flex', alignItems:'center', gap:'0.25rem', opacity:0.55, fontFamily:'Outfit', padding:0 }}
          onMouseEnter={e=>e.currentTarget.style.opacity=1}
          onMouseLeave={e=>e.currentTarget.style.opacity=0.55}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
          </svg>
          {lang === 'fr' ? 'Signaler' : 'Report'}
        </button>
      </div>}

      {/* Report modal */}
      {showReport && (
        <div className="overlay" onClick={()=>setShowReport(false)}>
          <div className="sheet" onClick={e=>e.stopPropagation()} style={{maxHeight:'65vh'}}>
            <div style={{textAlign:'center',padding:'0.6rem 0 0'}}>
              <div style={{width:32,height:2,background:'var(--muted2)',margin:'0 auto',borderRadius:'var(--r-sm)'}}/>
            </div>
            <div style={{padding:'1.2rem 1.2rem 1.8rem'}}>
              {!reportSent ? (<>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:'1.3rem',fontWeight:400,marginBottom:'0.3rem'}}>Report this profile</h3>
                <p style={{fontSize:'0.78rem',color:'var(--muted)',marginBottom:'1.2rem',lineHeight:1.6}}>{tr('discover.report_anon')}</p>
                {['Fake profile or impersonation','Inappropriate or offensive content','Spam or scam attempt','Harassment or threatening behaviour','Underage person','Other'].map(r=>(
                  <button key={r} onClick={()=>setReportReason(r)} style={{
                    display:'block',width:'100%',textAlign:'left',padding:'0.7rem 0.9rem',marginBottom:'0.4rem',
                    background:reportReason===r?'rgba(192,69,58,0.1)':'var(--dark)',
                    border:reportReason===r?'1px solid rgba(192,69,58,0.5)':'1px solid var(--border)',
                    color:reportReason===r?'#C0453A':'var(--white)',
                    cursor:'pointer',fontSize:'0.83rem',transition:'all 0.15s',
                  }}>{r}</button>
                ))}
                <button
                  className="btn btn-md btn-full"
                  disabled={!reportReason}
                  onClick={()=>setReportSent(true)}
                  style={{marginTop:'0.8rem',background:reportReason?'var(--red)':'var(--mid)', color:'var(--white)',border:'none',opacity:reportReason?1:0.4}}
                >{tr('discover.submit_report')}</button>
              </>) : (
                <div style={{textAlign:'center',padding:'1.5rem 0'}}>
                  <div style={{fontSize:'2.5rem',marginBottom:'0.8rem',color:'var(--gold)'}}>✓</div>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:'1.3rem',fontWeight:400,marginBottom:'0.5rem'}}>{tr('discover.report_recv')}</h3>
                  <p style={{fontSize:'0.82rem',color:'var(--muted)',lineHeight:1.6,marginBottom:'1.2rem'}}>
                    {tr('discover.report_thanks')}
                  </p>
                  <button className="btn btn-outline btn-md" onClick={()=>{ setShowReport(false); doSwipe('left'); }}>
                    Pass this profile too
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Referral sheet */}
      {showReferral && user && (
        <ReferralSheet profile={user} onClose={() => setShowReferral(false)}/>
      )}

            {/* Full profile sheet */}
      {showDetail && (detailUser || user) && (
        <ProfileSheet
          user={detailUser || user}
          onClose={() => { setShowDetail(false); setDetailUser(null); }}
          mode="discover"
          likes={likes}
          onPass={() => { doSwipe('left'); setShowDetail(false); }}
          onLike={() => { doSwipe('right'); setShowDetail(false); }}
          onWrite={() => { setShowDetail(false); setTimeout(() => { setActiveChat((detailUser || user).id); import('../utils/navigate').then(({ navigate }) => navigate('messages')); }, 150); }}
        />
      )}

      {/* ── It's a match overlay ── */}
      {matchOverlay && (
        <div
          onClick={() => setMatchOverlay(null)}
          style={{ position:'fixed', inset:0, zIndex:400, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width:'100%', maxWidth:480, background:'var(--charcoal)', borderTop:'1px solid var(--gold-border)', borderLeft:'1px solid var(--border)', borderRight:'1px solid var(--border)', borderRadius:'var(--r-xl) var(--r-xl) 0 0', padding:'0 0 2rem', animation:'sheetUp 0.35s cubic-bezier(0.32,0.72,0,1)', position:'relative', overflow:'hidden' }}
          >
            <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,var(--gold),transparent)' }}/>
            <div style={{ display:'flex', justifyContent:'center', padding:'0.8rem 0 0' }}>
              <div style={{ width:32, height:3, background:'var(--border)', borderRadius:'var(--r-sm)' }}/>
            </div>
            <div style={{ textAlign:'center', padding:'1.2rem 1.5rem 1.4rem' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2rem', fontWeight:600, color:'var(--gold)', letterSpacing:'0.04em', marginBottom:'0.25rem' }}>
                {tr('match.its_a_match')}
              </div>
              <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.6 }}>
                {tr('match.liked_each_other',{name:'__NAME__'}).split('__NAME__')[0]}<strong style={{color:'var(--white)'}}>{matchOverlay.name}</strong>{tr('match.liked_each_other',{name:'__NAME__'}).split('__NAME__')[1]}
              </p>
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'1rem', marginBottom:'1.5rem', padding:'0 1.5rem' }}>
              <div style={{ position:'relative' }}>
                <div style={{ width:88, height:88, borderRadius:'50%', overflow:'hidden', border:'2px solid var(--gold)' }}>
                  <img src={(currentUser.photos||'')[0] || 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200&q=80&auto=format&fit=crop'} alt="Vous" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                </div>
                <div style={{ position:'absolute', bottom:0, right:0, width:22, height:22, borderRadius:'50%', background:'var(--gold)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem' }}>♥</div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                {[0,1,2,3,4].map(i => <div key={i} style={{ width:4, height:4, borderRadius:'50%', background: i===2?'var(--gold)':'rgba(201,168,76,0.25)' }}/>)}
              </div>
              <div style={{ position:'relative' }}>
                <div style={{ width:88, height:88, borderRadius:'50%', overflow:'hidden', border:'2px solid var(--gold)' }}>
                  <img src={(matchOverlay.photos||'')[0]} alt={matchOverlay.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                </div>
                <div style={{ position:'absolute', bottom:0, left:0, width:22, height:22, borderRadius:'50%', background:'var(--gold)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem' }}>♥</div>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem', padding:'0 1.5rem' }}>
              <button
                onClick={() => { setMatchOverlay(null); setActiveChat(matchOverlay.id); import('../utils/navigate').then(({ navigate }) => navigate('messages')); }}
                className="btn btn-gold btn-full"
                style={{ padding:'0.9rem', fontSize:'0.85rem', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:600 }}
              >
                {tr('match.send_message')}
              </button>
              <button
                onClick={() => { const mu = matchOverlay; setMatchOverlay(null); setDetailUser(mu); setShowDetail(true); }}
                className="btn btn-dark btn-full"
                style={{ padding:'0.9rem', fontSize:'0.85rem', letterSpacing:'0.08em', textTransform:'uppercase', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                {tr('discover.view_profile')}
              </button>
              <button
                onClick={() => setMatchOverlay(null)}
                style={{ background:'none', border:'none', color:'var(--muted2)', fontSize:'0.75rem', cursor:'pointer', fontFamily:'Outfit', padding:'0.3rem', letterSpacing:'0.04em' }}
              >
                Keep Exploring
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Milestone celebration */}
      {pending && <MilestoneCelebration milestone={pending} onClose={clearPending}/>}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}